import { Alert, Box, Grid, Skeleton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  OrderActions,
  OrderHeader,
  OrderItemsTable,
  OrderSummary,
} from "../../components/backend/orders";
import AssignCargoCompanyModal from "../../components/backend/orders/AssignCargoCompanyModal";
import OrderStatusUpdateModal from "../../components/backend/orders/OrderStatusUpdateModal";
import { adminOrderItemService } from "../../services/adminOrderItemService";
import { adminOrderService } from "../../services/adminOrderService";
import { AddOrderItemRequest } from "../../types/admin";

import AdminPage from "./AdminPage";

interface OrderItem {
  id: string;
  productId: string;
  title: string;
  price: number;
  quantity: number;
  image?: string;
  sku: string;
  subtotal: number;
  status?: string;
  statusId?: number;
  notes?: string;
  retailer?: string;
  trackingNumber?: string;
  retailerOrderId?: string; // Retailer's order ID (Amazon, Walmart, etc.)
  retailerTrackingId?: string; // Retailer's tracking ID (Amazon, Walmart, etc.)
  estimatedDelivery?: string;
  orderedAt?: string;
  shippedAt?: string;
  isShipped?: boolean;
}

const OrderDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { orderNumber: orderId } = useParams<{ orderNumber: string }>();

  const [orderDetail, setOrderDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [itemStatuses, setItemStatuses] = useState<any[]>([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [orderStatusModalOpen, setOrderStatusModalOpen] = useState(false);
  const [cargoCompanyModalOpen, setCargoCompanyModalOpen] = useState(false);

  useEffect(() => {
    if (orderId) {
      loadOrderDetails();
      loadItemStatuses();
    }
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      const [summaryResponse, detailResponse] = await Promise.all([
        adminOrderItemService.getOrderSummary(orderId!),
        adminOrderService.getOrderDetail(orderId!),
      ]);
      const orderSummary = summaryResponse;
      const orderDetail = detailResponse;

      if (orderSummary?.items) {
        const rate = orderSummary.exchangeRateAtOrder;
        const updatedItems = orderSummary.items.map((item: any) => {
          // Use stored TJS or fall back to computing from exchange rate (for old orders)
          const priceTjs = item.priceTjs ?? (rate ? Math.round(item.price * rate * 100) / 100 : null);
          return {
            ...item,
            sku: item.productId, // Use ProductId as SKU for now
            subtotal: item.price * item.quantity,
            priceTjs,
            subtotalTjs: priceTjs ? priceTjs * item.quantity : null,
          };
        });
        setOrderDetail({
          ...orderSummary,
          ...orderDetail,
          totalAmount:
            orderSummary.totalAmount ?? orderDetail.totalAmount ?? 0,
          totalAmountTjs:
            orderSummary.totalAmountTjs ?? orderDetail.totalAmountTjs ?? null,
          tax: orderSummary.tax ?? orderDetail.tax ?? 0,
          taxTjs: orderSummary.taxTjs ?? orderDetail.taxTjs ?? null,
          fees: orderSummary.fees ?? orderDetail.fees ?? 0,
          feesTjs: orderSummary.feesTjs ?? orderDetail.feesTjs ?? null,
          finalTotal: orderSummary.finalTotal ?? orderDetail.finalTotal ?? 0,
          finalTotalTjs:
            orderSummary.finalTotalTjs ?? orderDetail.finalTotalTjs ?? null,
          discountAmount:
            orderSummary.discountAmount ?? orderDetail.discountAmount ?? null,
          discountAmountTjs:
            orderSummary.discountAmountTjs ??
            orderDetail.discountAmountTjs ??
            null,
          items: updatedItems,
        });
      } else {
        setOrderDetail({ ...orderSummary, ...orderDetail });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadItemStatuses = async () => {
    try {
      setLoadingStatuses(true);
      const statuses = await adminOrderItemService.getAvailableItemStatuses();
      setItemStatuses(statuses);
    } catch (error) {
    } finally {
      setLoadingStatuses(false);
    }
  };

  const handleUpdateItem = async (
    itemId: string,
    updates: Partial<OrderItem>
  ) => {
    try {
      // Call backend API to update order item (only dropshipping fields)
      await adminOrderItemService.updateOrderItem({
        orderItemId: itemId,
        ...updates,
      });

      // Update local state
      if (orderDetail?.items) {
        const updatedItems = orderDetail.items.map((item: OrderItem) => {
          if (item.id === itemId) {
            return { ...item, ...updates };
          }
          return item;
        });
        setOrderDetail({ ...orderDetail, items: updatedItems });
      }
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateStatus = async (
    itemId: string,
    newStatusId: number,
    notes?: string,
    sendNotification?: boolean
  ) => {
    try {
      await adminOrderItemService.updateItemStatus(
        itemId,
        newStatusId,
        notes,
        sendNotification
      );

      // Update local state
      if (orderDetail?.items) {
        const updatedItems = orderDetail.items.map((item: OrderItem) => {
          if (item.id === itemId) {
            // Find the status name for the new status ID
            const newStatus = itemStatuses.find((s) => s.id === newStatusId);
            return {
              ...item,
              status: newStatus?.name || "Unknown",
              statusId: newStatusId,
              notes,
            };
          }
          return item;
        });
        setOrderDetail({ ...orderDetail, items: updatedItems });
      }
    } catch (error) {
      throw error;
    }
  };

  const handleAddItem = async (request: AddOrderItemRequest) => {
    try {
      await adminOrderItemService.addOrderItem(request);
      await loadOrderDetails();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteItem = async (orderItemId: string) => {
    try {
      await adminOrderItemService.deleteOrderItem(orderItemId);
      await loadOrderDetails();
    } catch (error) {
      throw error;
    }
  };

  const handleEditOrder = () => {
    // TODO: Implement edit order functionality
  };

  const handleEditItem = (item: OrderItem) => {
    // This will be handled by the OrderActions component
  };

  const handleViewItem = (item: OrderItem) => {
    // TODO: Implement view item details
  };

  const handleOrderStatusClick = () => {
    setOrderStatusModalOpen(true);
  };

  const handleOrderStatusUpdate = async (
    orderId: string,
    newStatusId: number,
    notes?: string,
    sendNotification?: boolean,
    selectedItemIds?: string[],
    itemStatusId?: number,
  ) => {
    try {
      const request = {
        newStatusId,
        statusChangeNotes: notes,
        sendNotification: sendNotification ?? true,
        orderItemIds: selectedItemIds,
        itemStatusId,
      };
      // Call the backend API to update order status using service
      await adminOrderService.updateOrderStatus(orderId, request);

      // Reload order details to get updated status
      await loadOrderDetails();
    } catch (error) {
      throw error;
    }
  };

  if (loading) {
    return (
      <AdminPage>
        <Box sx={{ p: 3 }}>
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rectangular" height={400} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          </Grid>
        </Box>
      </AdminPage>
    );
  }

  if (!orderDetail) {
    return (
      <AdminPage>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{t("admin.orderDetails.orderNotFound")}</Alert>
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Box sx={{ p: 3 }}>
        {/* Order Header */}
        <OrderHeader
          orderNumber={orderDetail.orderNumber || orderId}
          createdAt={orderDetail.createdAt}
          updatedAt={orderDetail.updatedAt}
          status={orderDetail.status}
          paymentStatus={orderDetail.paymentStatus}
          cargoCompanyName={orderDetail.cargoCompanyName}
          cargoCompanyId={orderDetail.cargoCompanyId}
          onEditOrder={handleEditOrder}
          onAssignCargoCompany={() => setCargoCompanyModalOpen(true)}
        />

        {/* Order Summary */}
        <Box sx={{ mb: 3 }}>
          <OrderSummary
            userFirstName={orderDetail.userFirstName}
            userLastName={orderDetail.userLastName}
            userId={orderDetail.userId}
            userEmail={orderDetail.userEmail}
            userPhoneNumber={orderDetail.userPhoneNumber}
            userCountry={orderDetail.userCountry}
            userProfilePicture={orderDetail.userProfilePicture}
            deliveryInfo={orderDetail.deliveryInfo}
            paymentInfo={orderDetail.paymentInfo}
            totalAmount={orderDetail.totalAmount}
            shippingCost={orderDetail.shippingCost}
            tax={orderDetail.tax}
            fees={orderDetail.fees}
            discountAmount={orderDetail.discountAmount}
            finalTotal={orderDetail.finalTotal}
            itemCount={orderDetail.itemCount}
            exchangeRateAtOrder={orderDetail.exchangeRateAtOrder}
            totalAmountTjs={orderDetail.totalAmountTjs}
            shippingCostTjs={orderDetail.shippingCostTjs}
            taxTjs={orderDetail.taxTjs}
            feesTjs={orderDetail.feesTjs}
            discountAmountTjs={orderDetail.discountAmountTjs}
            finalTotalTjs={orderDetail.finalTotalTjs}
          />
        </Box>

        {/* Order Items Table */}
        <Box sx={{ mb: 3 }}>
          <OrderItemsTable
            items={orderDetail.items || []}
            orderId={orderDetail.orderId || orderId!}
            onEditItem={handleEditItem}
            onViewItem={handleViewItem}
            onStatusUpdate={handleUpdateStatus}
            onUpdateItem={handleUpdateItem}
            onAddItem={handleAddItem}
            onDeleteItem={handleDeleteItem}
            onBulkStatusUpdate={loadOrderDetails}
          />
        </Box>

        {/* Order Actions - Modals */}
        <OrderActions
          itemStatuses={itemStatuses}
          onUpdateItem={handleUpdateItem}
          onUpdateStatus={handleUpdateStatus}
        />

        {/* Order Status Update Modal */}
        <OrderStatusUpdateModal
          open={orderStatusModalOpen}
          onClose={() => setOrderStatusModalOpen(false)}
          orderId={orderDetail.orderId || orderId}
          orderNumber={orderDetail.orderNumber || orderId}
          currentStatus={orderDetail.status}
          orderItems={orderDetail.items || []}
          onStatusUpdate={handleOrderStatusUpdate}
        />

        {/* Assign Cargo Company Modal */}
        <AssignCargoCompanyModal
          open={cargoCompanyModalOpen}
          onClose={() => setCargoCompanyModalOpen(false)}
          orderId={orderDetail.orderId || orderId}
          currentCargoCompanyId={orderDetail.cargoCompanyId}
          currentCargoCompanyName={orderDetail.cargoCompanyName}
          hasShippedItems={
            orderDetail.items?.some((item: OrderItem) => item.isShipped) ||
            false
          }
          onSuccess={async () => {
            await loadOrderDetails();
          }}
        />
      </Box>
    </AdminPage>
  );
};

export default OrderDetailsPage;
